import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ChatProvider } from '../../context/ChatContext';
import { useChat } from '../../context/ChatContext';
import Sidebar     from '../../components/Sidebar';
import ChatWindow  from '../../components/ChatWindow';
import NotesPanel  from '../../components/NotesPanel';
import UsersPage   from '../admin/UsersPage';
import OffersPage  from '../admin/OffersPage';
import GamesPage   from '../admin/GamesPage';
import BulkMessagingPage from '../admin/BulkMessagingPage';
import SavedRepliesPage from '../admin/SavedRepliesPage';

const NAV = [
  { id: 'chat',   icon: '💬', label: 'Chat' },
  { id: 'users',  icon: '👥', label: 'Users' },
  { id: 'offers', icon: '🎁', label: 'Offers' },
  { id: 'games',  icon: '🎮', label: 'Games' },
  { id: 'bulk',   icon: '📢', label: 'Bulk' },
  { id: 'replies', icon: '📋', label: 'Replies' },
];

// Inner component so it can access useChat
function AdminInner() {
  const { logout } = useAuth();
  const { activeUser, selectUser, totalUnread } = useChat();
  const navigate   = useNavigate();
  const [activePage, setActivePage] = useState('chat');
  const [isMobile, setIsMobile]     = useState(window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleLogout = () => { logout(); navigate('/login', { replace: true }); };

  // On mobile chat tab:
  // — no user selected → show sidebar (user list)
  // — user selected    → show chat window with back button
  const showMobileSidebar = isMobile && activePage === 'chat' && !activeUser;
  const showMobileChat    = isMobile && activePage === 'chat' && !!activeUser;

  const renderPage = () => {
    // ── Desktop chat layout ──────────────────────────────────────────────────
    if (!isMobile && activePage === 'chat') {
      return (
        <div style={s.chatLayout}>
          <div style={s.chatSidebar}>
            <Sidebar />
          </div>
          <div style={s.chatMain}>
            <ChatWindow />
          </div>
          <div style={s.notesPanelWrap}>
            <NotesPanel user={activeUser} />
          </div>
        </div>
      );
    }

    // ── Mobile: show sidebar (user list) ─────────────────────────────────────
    if (showMobileSidebar) {
      return (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Sidebar onSelectUser={() => {}} />
        </div>
      );
    }

    // ── Mobile: show chat window with back button ────────────────────────────
    if (showMobileChat) {
      return (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Back button */}
          <div style={s.mobileBackBar}>
            <button style={s.backBtn} onClick={() => selectUser(null)}>
              ← Back
            </button>
            <span style={s.backTitle}>{activeUser?.name}</span>
          </div>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <ChatWindow />
          </div>
        </div>
      );
    }

    // ── Other pages ──────────────────────────────────────────────────────────
    const pages = {
      users:  <UsersPage />,
      offers: <OffersPage />,
      games:  <GamesPage />,
      bulk:   <BulkMessagingPage />,
      replies: <SavedRepliesPage />,
    };
    return <div style={s.pageWrap}>{pages[activePage]}</div>;
  };

  return (
    <div style={s.root}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@700&family=Exo+2:wght@400;600;700;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;}
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-thumb{background:rgba(255,179,0,.2);border-radius:4px}
        .anav:hover{background:rgba(255,179,0,.06)!important;color:rgba(255,255,255,.8)!important}
      `}</style>

      {/* Desktop left nav */}
      {!isMobile && (
        <div style={s.leftNav}>
          <div style={s.brand}>
            <img src="/logo.jpg" alt="AIA74" style={{ height: '42px', width: 'auto', objectFit: 'contain' }} />
          </div>
          <div style={s.adminCard}>
            <div style={s.adminAvatar}>AD</div>
            <div>
              <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '600' }}>Admin Panel</div>
              <div style={{ color: 'rgba(255,179,0,.6)', fontSize: '10px', marginTop: '2px' }}>⭐ Administrator</div>
            </div>
          </div>
          <nav style={s.nav}>
            {NAV.map(item => (
              <button key={item.id} className="anav" onClick={() => setActivePage(item.id)}
                style={{ ...s.navItem, ...(activePage === item.id ? s.navActive : {}) }}>
                <span style={{ fontSize: '17px', width: '22px' }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.id === 'chat' && totalUnread > 0 && (
                  <span style={s.navBadge}>{totalUnread > 99 ? '99+' : totalUnread}</span>
                )}
              </button>
            ))}
          </nav>
          <button style={s.logoutBtn} onClick={handleLogout}>⏻ &nbsp;Logout</button>
        </div>
      )}

      {/* Mobile top bar */}
      {isMobile && (
        <div style={s.mobileTop}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <img src="/logo.jpg" alt="AIA74" style={{ height: '38px', width: 'auto', objectFit: 'contain' }} />
          </div>
          <button style={s.mobileLogout} onClick={handleLogout}>⏻</button>
        </div>
      )}

      {/* Main content */}
      <div style={{ ...s.main, ...(isMobile ? s.mainMobile : {}) }}>
        {renderPage()}
      </div>

      {/* Mobile bottom nav — hide when in chat window */}
      {isMobile && !showMobileChat && (
        <div style={s.bottomNav}>
          {NAV.map(item => (
            <button
              key={item.id}
              onClick={() => setActivePage(item.id)}
              style={{ ...s.bottomTab, ...(activePage === item.id ? s.bottomTabActive : {}) }}
            >
              <span style={{ fontSize: '20px' }}>{item.icon}</span>
              <span style={{ fontSize: '9px', marginTop: '2px' }}>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminApp() {
  return (
    <ChatProvider>
      <AdminInner />
    </ChatProvider>
  );
}

const s = {
  root:           { display: 'flex', height: '100vh', overflow: 'hidden', background: '#080c14', fontFamily: "'Exo 2',sans-serif" },
  leftNav:        { width: '210px', minWidth: '210px', background: '#0d1420', borderRight: '1px solid rgba(255,179,0,.1)', display: 'flex', flexDirection: 'column', height: '100vh', flexShrink: 0 },
  brand:          { display: 'flex', alignItems: 'center', gap: '9px', padding: '18px 14px', borderBottom: '1px solid rgba(255,179,0,.08)' },
  brandText:      { fontFamily: "'Rajdhani',sans-serif", fontSize: '19px', fontWeight: '700', color: '#FFB300', letterSpacing: '5px' },
  adminCard:      { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', margin: '10px', borderRadius: '12px', background: 'rgba(255,179,0,.04)', border: '1px solid rgba(255,179,0,.08)' },
  adminAvatar:    { width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,179,0,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFB300', fontSize: '12px', fontWeight: '900', flexShrink: 0 },
  nav:            { flex: 1, padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' },
  navItem:        { display: 'flex', alignItems: 'center', gap: '9px', padding: '11px 12px', borderRadius: '11px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,.4)', cursor: 'pointer', textAlign: 'left', width: '100%', fontSize: '13px', fontWeight: '600', fontFamily: "'Exo 2',sans-serif", transition: 'all .15s' },
  navActive:      { background: 'rgba(255,179,0,.08)', color: '#FFB300', borderLeft: '3px solid #FFB300' },
  logoutBtn:      { margin: '12px', background: 'rgba(255,50,50,.07)', border: '1px solid rgba(255,50,50,.15)', borderRadius: '10px', color: '#ff6b6b', padding: '10px', cursor: 'pointer', fontSize: '13px', fontFamily: "'Exo 2',sans-serif" },
  mobileTop:      { position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: '52px', background: '#0d1420', borderBottom: '1px solid rgba(255,179,0,.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px' },
  mobileLogout:   { background: 'rgba(255,50,50,.08)', border: '1px solid rgba(255,50,50,.15)', borderRadius: '8px', color: '#ff6b6b', fontSize: '15px', cursor: 'pointer', padding: '5px 9px' },
  main:           { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  mainMobile:     { paddingTop: '52px', paddingBottom: '60px' },
  pageWrap:       { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  chatLayout:     { display: 'flex', height: '100%', overflow: 'hidden' },
  chatSidebar:    { display: 'flex', flexShrink: 0 },
  chatMain:       { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 },
  notesPanelWrap: { display: 'flex', flexShrink: 0, overflow: 'hidden' },
  mobileBackBar:  { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: '#0d1420', borderBottom: '1px solid rgba(255,179,0,.1)', flexShrink: 0 },
  backBtn:        { background: 'rgba(255,179,0,.08)', border: '1px solid rgba(255,179,0,.2)', borderRadius: '8px', color: '#FFB300', padding: '6px 12px', cursor: 'pointer', fontSize: '13px', fontFamily: "'Exo 2',sans-serif", fontWeight: '600', flexShrink: 0 },
  backTitle:      { color: '#e2e8f0', fontSize: '14px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  bottomNav:      { position: 'fixed', bottom: 0, left: 0, right: 0, height: '60px', background: '#0d1420', borderTop: '1px solid rgba(255,179,0,.1)', display: 'flex', zIndex: 100 },
  bottomTab:      { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', fontFamily: "'Exo 2',sans-serif", transition: 'all .15s' },
  bottomTabActive:{ color: '#FFB300', background: 'rgba(255,179,0,.05)' },
  navBadge:   { background: 'linear-gradient(135deg,#FFB300,#00aaff)', color: '#080c14', fontSize: '10px', fontWeight: '900', borderRadius: '10px', padding: '2px 7px', minWidth: '18px', textAlign: 'center' },
  mobileBadge:{ position: 'absolute', top: '-6px', right: '-8px', background: 'linear-gradient(135deg,#FFB300,#00aaff)', color: '#080c14', fontSize: '8px', fontWeight: '900', borderRadius: '8px', padding: '1px 5px', minWidth: '14px', textAlign: 'center' },
};
